// src/utils/groupByDate.js
import {
  format,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  isThisYear,
  subMonths,
  isSameMonth,
} from 'date-fns';

/**
 * Groups items by creation date into temporal sections.
 * Reused by Notes list and Gallery.
 * @param {Array} items - Array of objects with a `creationDate` Firestore Timestamp field.
 * @param {Function} t - Translation function from i18next.
 * @returns {Array} Array of [sectionName, itemsArray] pairs.
 */
export function groupByDate(items, t) {
  const groupKeysMap = {
    'today': t('notes.groups.today'),
    'yesterday': t('notes.groups.yesterday'),
    'thisweek': t('notes.groups.thisweek'),
    'thismonth': t('notes.groups.thismonth'),
    'lastmonth': t('notes.groups.lastmonth'),
    'thisyear': t('notes.groups.thisyear'),
  };

  const groups = {
    [groupKeysMap.today]: [],
    [groupKeysMap.yesterday]: [],
    [groupKeysMap.thisweek]: [],
    [groupKeysMap.thismonth]: [],
    [groupKeysMap.lastmonth]: [],
    [groupKeysMap.thisyear]: [],
  };

  const now = new Date();
  const lastMonth = subMonths(now, 1);

  items.forEach((item) => {
    const date = item.creationDate?.toDate?.() || new Date();
    if (isToday(date)) groups[groupKeysMap.today].push(item);
    else if (isYesterday(date)) groups[groupKeysMap.yesterday].push(item);
    else if (isThisWeek(date)) groups[groupKeysMap.thisweek].push(item);
    else if (isThisMonth(date)) groups[groupKeysMap.thismonth].push(item);
    else if (isSameMonth(date, lastMonth)) groups[groupKeysMap.lastmonth].push(item);
    else if (isThisYear(date)) groups[groupKeysMap.thisyear].push(item);
    else {
      const year = format(date, 'yyyy');
      if (!groups[year]) groups[year] = [];
      groups[year].push(item);
    }
  });

  const orderedSections = [
    groupKeysMap.today,
    groupKeysMap.yesterday,
    groupKeysMap.thisweek,
    groupKeysMap.thismonth,
    groupKeysMap.lastmonth,
    groupKeysMap.thisyear
  ];

  const yearSections = Object.keys(groups)
    .filter(key => !orderedSections.includes(key))
    .sort((a, b) => parseInt(b) - parseInt(a));

  const allSections = [...orderedSections, ...yearSections];

  return allSections.map(section => [section, groups[section] || []]);
}
